import { type Node } from '@xyflow/react';
import {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useKey, useLatest } from 'react-use';

import {
  DiagramEditorTool,
  NEW_NODE_BY_TOOL,
} from '../components/DiagramEditor/diagramEditorTools';
import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import { useReactFlowDiagram } from '../hooks/useReactFlowDiagram';
import { getConnectedNodePosition } from '../utils/diagramConnections';
import {
  addDiagramConnection,
  addDiagramNode,
  addDiagramStageBoundary,
  editDiagramEdge,
  editDiagramNode,
  editDiagramStageLabel,
  moveDiagramStageBoundary,
  pinDiagramPositions,
  placeDiagramNode,
  reconnectDiagramEdge,
  removeDiagramElement,
  removeDiagramStageBoundary,
  resetDiagramPositions,
} from '../utils/diagramEdits';
import { getStageIDAt, type StageBoundary } from '../utils/diagramStages';
import {
  type DiagramEditing,
  type DiagramEditingActions,
  DiagramEditingContext,
  type DiagramLabelTarget,
} from './diagramEditing.context';

/** The editor does not add more tiles than this. */
const MAX_NODES = 80;
/** The editor does not add more connections than this. */
const MAX_EDGES = 160;

interface DiagramEditingProviderProps {
  diagram: DiagramBuilder_DiagramFragment;
  onChange: (diagram: DiagramBuilder_DiagramFragment) => void;
  locked: boolean;
}

function useContextValue({
  diagram,
  onChange,
  locked,
}: DiagramEditingProviderProps): DiagramEditing {
  const [selectedTool, setTool] = useState<DiagramEditorTool>(
    DiagramEditorTool.Select
  );
  const [labelTarget, setLabelTarget] = useState<DiagramLabelTarget | null>(
    null
  );
  const [notesEditorNodeID, setNotesEditorNodeID] = useState<string | null>(
    null
  );
  // The actions read the props from this ref, not from the render that made
  // them. Thus the actions keep their identity, and the tiles and controls
  // that get them do not render again after each edit. A stale popover or
  // drag callback also reads the latest lock, so it cannot edit a locked
  // diagram.
  const latest = useLatest({ diagram, onChange, locked });
  // The canvas tiles and stage dividers, after the automatic layout places
  // them. Null before then. A structural edit saves these positions.
  const placed = useRef<{
    tiles: Node[];
    boundaries: StageBoundary[];
  } | null>(null);

  const actions = useMemo((): DiagramEditingActions => {
    /** Gives `change(latest diagram)` to `onChange`, unless the canvas is locked. */
    const edit = (
      change: (
        diagram: DiagramBuilder_DiagramFragment
      ) => DiagramBuilder_DiagramFragment
    ) => {
      const current = latest.current;
      if (!current.locked) current.onChange(change(current.diagram));
    };
    /**
     * Applies an edit that adds a node, changes a connection, or changes the
     * stages. These edits change the input of the automatic layout, so the
     * edit first saves the tile positions and divider heights that the user
     * sees. Otherwise the layout moves them. The change gets the canvas
     * tiles, with their measured sizes.
     */
    const editStructure = (
      change: (
        diagram: DiagramBuilder_DiagramFragment,
        tiles: Node[]
      ) => DiagramBuilder_DiagramFragment
    ) => {
      const canvas = placed.current;
      edit((current) =>
        canvas
          ? change(
              pinDiagramPositions(current, canvas.tiles, canvas.boundaries),
              canvas.tiles
            )
          : change(current, [])
      );
    };
    /** The ID of the stage at canvas height `y`, by the dividers the user sees. */
    const stageAt = (y: number) =>
      getStageIDAt(
        latest.current.diagram.content.stages,
        placed.current?.boundaries ?? [],
        y
      );
    const findNode = (id: string) =>
      latest.current.diagram.content.nodes.find((node) => node.nodeID === id);
    const canConnect = (sourceNodeID: string, targetNodeID: string) =>
      sourceNodeID !== targetNodeID &&
      !!findNode(sourceNodeID) &&
      !!findNode(targetNodeID) &&
      latest.current.diagram.content.edges.length < MAX_EDGES;
    const newEdgeID = () => `edge-${crypto.randomUUID()}`;
    return {
      update: (next) => edit(() => next),
      setTool,
      canConnect,
      connect: (ends) => {
        if (!canConnect(ends.sourceNodeID, ends.targetNodeID)) return;
        editStructure((current) =>
          addDiagramConnection(current, newEdgeID(), ends)
        );
      },
      reconnect: (edgeID, ends) => {
        if (ends.sourceNodeID === ends.targetNodeID) return;
        editStructure((current) => reconnectDiagramEdge(current, edgeID, ends));
      },
      addConnectedNode: (source, position, tool) => {
        const sourceNode = findNode(source.nodeID);
        const { content } = latest.current.diagram;
        if (
          !sourceNode ||
          content.nodes.length >= MAX_NODES ||
          content.edges.length >= MAX_EDGES
        ) {
          return;
        }
        const nodeID = `node-${crypto.randomUUID()}`;
        const node = NEW_NODE_BY_TOOL[tool];
        const at = getConnectedNodePosition(source.side, position, tool);
        editStructure((current) =>
          addDiagramConnection(
            addDiagramNode(
              current,
              nodeID,
              at.position,
              node,
              stageAt(at.position.y) ?? sourceNode.stageId
            ),
            newEdgeID(),
            {
              sourceNodeID: source.nodeID,
              sourceSide: source.side,
              targetNodeID: nodeID,
              targetSide: at.side,
            }
          )
        );
      },
      placeElement: (tool, position) => {
        setTool(DiagramEditorTool.Select);
        if (tool === DiagramEditorTool.StageDivider) {
          editStructure((current, tiles) =>
            addDiagramStageBoundary(
              current,
              {
                first: `stage-${crypto.randomUUID()}`,
                next: `stage-${crypto.randomUUID()}`,
              },
              position.y,
              tiles
            )
          );
          return;
        }
        if (latest.current.diagram.content.nodes.length >= MAX_NODES) return;
        editStructure((current) =>
          addDiagramNode(
            current,
            `node-${crypto.randomUUID()}`,
            position,
            NEW_NODE_BY_TOOL[tool],
            stageAt(position.y)
          )
        );
      },
      resetPositions: () => edit(resetDiagramPositions),
      placeNode: (tile, tiles, movedIDs) =>
        editStructure((current) =>
          placeDiagramNode(current, tile, tiles, movedIDs)
        ),
      openLabelEditor: setLabelTarget,
      closeLabelEditor: () => setLabelTarget(null),
      openNotesEditor: setNotesEditorNodeID,
      closeNotesEditor: (nodeID) =>
        setNotesEditorNodeID((current) =>
          current === nodeID ? null : current
        ),
      editNode: (id, changes) =>
        edit((current) => editDiagramNode(current, id, changes)),
      editEdge: (id, changes) =>
        edit((current) => editDiagramEdge(current, id, changes)),
      editStageLabel: (stageID, label) =>
        editStructure((current) =>
          editDiagramStageLabel(current, stageID, label)
        ),
      moveStageBoundary: (stageID, y) =>
        editStructure((current, tiles) =>
          moveDiagramStageBoundary(current, stageID, y, tiles)
        ),
      removeStageBoundary: (stageID) =>
        editStructure((current, tiles) =>
          removeDiagramStageBoundary(current, stageID, tiles)
        ),
      removeElements: (...ids) =>
        edit((current) => ids.reduce(removeDiagramElement, current)),
    };
  }, [latest]);
  // Each tile calls `removeElements` from its Delete button.
  const reactFlowDiagram = useReactFlowDiagram(diagram, actions.removeElements);
  useEffect(() => {
    placed.current = reactFlowDiagram.isPlaced
      ? {
          tiles: reactFlowDiagram.nodes,
          boundaries: reactFlowDiagram.stageBoundaries,
        }
      : null;
  }, [
    reactFlowDiagram.isPlaced,
    reactFlowDiagram.nodes,
    reactFlowDiagram.stageBoundaries,
  ]);
  // Escape cancels a placement or connection tool. An inline text editor
  // stops its own Escape key, so the key ends the edit instead.
  useKey('Escape', () => setTool(DiagramEditorTool.Select));

  return {
    ...actions,
    diagram,
    locked,
    reactFlowDiagram,
    tool: locked ? DiagramEditorTool.Select : selectedTool,
    labelTarget,
    notesEditorNodeID,
    canAddNode: diagram.content.nodes.length < MAX_NODES,
  };
}

/**
 * Provides the React Flow diagram and the edit actions for one diagram. Every edit
 * creates a new diagram and passes it to `onChange`.
 */
export function DiagramEditingProvider({
  children,
  ...props
}: PropsWithChildren<DiagramEditingProviderProps>) {
  const value = useContextValue(props);
  return (
    <DiagramEditingContext.Provider value={value}>
      {children}
    </DiagramEditingContext.Provider>
  );
}
